import NextLink from "next/link";
import React, {
    useCallback,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { cn } from "../../helpers/classNames";
import { useIsPageChange } from "../../hooks/useIsPageChange";
import type { SearchResultItem } from "../../network/matlocq/types/search";
import { useCountry, useIsWebview } from "../ApplicationState/hooks";
import { getSuggestionLink } from "./helpers";
import { ItemLoading } from "./itemLoading";
import styles from "./suggestList.module.scss";

type Props = {
    item: SearchResultItem;
    searchValue: string;
    suggestionClicked: boolean;
    setSuggestionClicked: Dispatch<SetStateAction<boolean>>;
};

export const SuggestListItem: React.FC<Props> = ({
    item,
    searchValue,
    suggestionClicked,
    setSuggestionClicked,
}) => {
    const isWebview = useIsWebview();
    const country = useCountry();
    const isPageChange = useIsPageChange();
    const [isLoading, setIsLoading] = useState(() => {
        if (isPageChange) {
            setSuggestionClicked(false);
            return false;
        }

        return false;
    });

    const title = useMemo(() => {
        // Escape "(" and ")" to have a working RegExp. Otherwise, RegExp will crash if user enters a brace.
        const parts = item.title.split(
            new RegExp(
                `(${searchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                "gi"
            )
        );

        return (
            <span className="title">
                {parts.map((part, index) =>
                    part.toLowerCase() === searchValue.toLowerCase() ? (
                        <b key={index}>{part}</b>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    }, [item.title, searchValue]);

    const href = getSuggestionLink(item, isWebview, country);
    const isNextLink =
        ["at", "pl"].includes(country) ||
        [
            // "locations",
            // "livecams",
            "skiAreas",
        ].includes(item.type);

    const onClick = useCallback((): void => {
        if (!suggestionClicked) {
            setIsLoading(true);
            setSuggestionClicked(true);
        }
    }, [suggestionClicked, setSuggestionClicked]);

    // TODO: when all links are moved to NextLink, reconsider using useLinkStatus-hook and onNavigate event.
    return (
        <li className={styles.item}>
            {isLoading ? (
                <ItemLoading />
            ) : suggestionClicked ? (
                <span className={cn(styles.inactive, styles.itemInner)}>
                    {title}
                </span>
            ) : isNextLink ? (
                <NextLink
                    href={href}
                    role="button"
                    className={styles.itemInner}
                    onNavigate={onClick}
                    tabIndex={0} // important to have proper working blur event
                >
                    {title}
                </NextLink>
            ) : (
                <a
                    href={href}
                    role="button"
                    className={styles.itemInner}
                    onClick={onClick}
                    tabIndex={0} // important to have proper working blur event
                >
                    {title}
                </a>
            )}
        </li>
    );
};
